import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Materials Management Component
export const MaterialsManagement = () => {
  const theme = useThemeClasses();
  const [materials, setMaterials] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_standard: true,
    order: 0,
    is_active: true,
    unit_of_measure: 'units',
    stock_quantity: 0,
    low_stock_threshold: 5,
    pricing_type: 'per_unit',
    cost_per_unit: 0,
    purchase_quantity: null,
    purchase_price: null
  });

  useEffect(() => {
    loadMaterials();
  }, []);

  const loadMaterials = async () => {
    try {
      const response = await axios.get(`${API}/admin/materials`);
      setMaterials(response.data);
    } catch (error) {
      console.error('Failed to load materials:', error);
      toast.error('Failed to load materials');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMaterial) {
        await axios.put(`${API}/admin/materials/${editingMaterial.id}`, formData);
        toast.success('Material updated successfully');
      } else {
        await axios.post(`${API}/admin/materials`, formData);
        toast.success('Material added successfully');
      }

      setFormData({ 
        name: '', 
        description: '', 
        is_standard: true, 
        order: 0, 
        is_active: true,
        unit_of_measure: 'units',
        stock_quantity: 0,
        low_stock_threshold: 5,
        pricing_type: 'per_unit',
        cost_per_unit: 0,
        purchase_quantity: null,
        purchase_price: null
      });
      setShowAddForm(false);
      setEditingMaterial(null);
      loadMaterials();
    } catch (error) {
      console.error('Failed to save material:', error);
      toast.error('Failed to save material');
    }
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormData({
      name: material.name,
      description: material.description || '',
      is_standard: material.is_standard,
      order: material.order || 0,
      is_active: material.is_active,
      unit_of_measure: material.unit_of_measure || 'units',
      stock_quantity: material.stock_quantity || 0,
      low_stock_threshold: material.low_stock_threshold || 5,
      pricing_type: material.pricing_type || 'per_unit',
      cost_per_unit: material.cost_per_unit || 0,
      purchase_quantity: material.purchase_quantity || null,
      purchase_price: material.purchase_price || null
    });
    setShowAddForm(true);
  };

  const handleDelete = async (materialId, materialName) => {
    if (!window.confirm(`Are you sure you want to delete "${materialName}"? This action cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/admin/materials/${materialId}`);
      toast.success('Material deleted successfully');
      loadMaterials();
    } catch (error) {
      console.error('Failed to delete material:', error);
      toast.error('Failed to delete material');
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingMaterial(null);
    setFormData({ 
      name: '', 
      description: '', 
      is_standard: true, 
      order: 0, 
      is_active: true,
      unit_of_measure: 'units',
      stock_quantity: 0,
      low_stock_threshold: 5,
      pricing_type: 'per_unit',
      cost_per_unit: 0,
      purchase_quantity: null,
      purchase_price: null
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Materials Management</h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          {showAddForm ? 'Cancel' : 'Add Material'}
        </button>
      </div>

      {showAddForm && (
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-lg font-semibold mb-4">
            {editingMaterial ? 'Edit Material' : 'Add New Material'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Material Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="e.g., PLA, PETG, Wood"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Display Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  min="0"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Description (Optional)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                rows={3}
                placeholder="Brief description of this material..."
              />
            </div>

            {/* Bulk Purchase Settings */}
            <div className={`${theme.bgSecondary} p-4 rounded-lg border ${theme.border}`}>
              <h4 className="text-sm font-semibold mb-3">Inventory & Pricing</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Unit of Measure</label>
                  <input
                    type="text"
                    value={formData.unit_of_measure || 'units'}
                    onChange={(e) => setFormData({...formData, unit_of_measure: e.target.value})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                    placeholder="e.g., kg, rolls, sheets"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Stock Quantity</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.stock_quantity || 0}
                    onChange={(e) => setFormData({...formData, stock_quantity: parseFloat(e.target.value) || 0})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Low Stock Threshold</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.low_stock_threshold || 5}
                    onChange={(e) => setFormData({...formData, low_stock_threshold: parseFloat(e.target.value) || 5})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                    min="0"
                  />
                </div>
              </div>

              {/* Pricing Type Selection */}
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">Pricing Type</label>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="pricing_type"
                      value="per_unit"
                      checked={formData.pricing_type === 'per_unit' || !formData.pricing_type}
                      onChange={(e) => setFormData({...formData, pricing_type: e.target.value})}
                      className="mr-2"
                    />
                    <span className="text-sm">Per Unit</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="pricing_type"
                      value="bulk_purchase"
                      checked={formData.pricing_type === 'bulk_purchase'}
                      onChange={(e) => setFormData({...formData, pricing_type: e.target.value})}
                      className="mr-2"
                    />
                    <span className="text-sm">Bulk Purchase (Packs/Bundles)</span>
                  </label>
                </div>
              </div>

              {/* Bulk Purchase Fields */}
              {formData.pricing_type === 'bulk_purchase' && (
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
                    💡 For materials purchased in packs/bundles. Total units = Stock Quantity × Pieces per Pack
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium mb-1">Pieces per Pack/Bundle</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.purchase_quantity || ''}
                        onChange={(e) => setFormData({...formData, purchase_quantity: parseFloat(e.target.value) || null})}
                        className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                        placeholder="e.g., 100"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Price per Pack ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.purchase_price || ''}
                        onChange={(e) => setFormData({...formData, purchase_price: parseFloat(e.target.value) || null})}
                        className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                        placeholder="e.g., 20.00"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Cost per Unit (Auto)</label>
                      <input
                        type="text"
                        value={
                          formData.purchase_quantity && formData.purchase_price
                            ? `$${(formData.purchase_price / formData.purchase_quantity).toFixed(4)}`
                            : '$0.00'
                        }
                        disabled
                        className={`w-full px-3 py-2 border ${theme.border} rounded bg-gray-100 dark:bg-gray-800 text-sm`}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Per Unit Pricing */}
              {formData.pricing_type === 'per_unit' && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Cost per Unit ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.cost_per_unit || 0}
                      onChange={(e) => setFormData({...formData, cost_per_unit: parseFloat(e.target.value) || 0})}
                      className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                      min="0"
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_standard"
                  checked={formData.is_standard}
                  onChange={(e) => setFormData({...formData, is_standard: e.target.checked})}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="is_standard" className="text-sm font-medium">Standard Material</label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="is_active" className="text-sm font-medium">Active Material</label>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <button
                type="submit"
                className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
              >
                {editingMaterial ? 'Update Material' : 'Add Material'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className={`${theme.bgSecondary}0 text-white px-6 py-2 rounded hover:bg-gray-600`}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Materials Table */}
      <div className={`${theme.card} rounded-lg shadow overflow-hidden`}>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Materials ({materials.length})</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={`${theme.bgSecondary}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Name</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Description</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Stock Qty</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Pieces Available</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Type</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Order</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Status</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`${theme.card} divide-y divide-gray-200`}>
                {materials.map((material) => (
                  <tr key={material.id} className={`hover:bg-gray-100 dark:hover:bg-gray-700`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${theme.textPrimary}`}>{material.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm ${theme.textPrimary} max-w-xs truncate`}>
                        {material.description || 'No description'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${theme.textPrimary}`}>
                        {material.pricing_type === 'bulk_purchase' && material.purchase_quantity ? (
                          <div className="space-y-1">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                              ((material.stock_quantity || 0) * (material.purchase_quantity || 1)) <= (material.low_stock_threshold || 5)
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            }`}>
                              {((material.stock_quantity || 0) * (material.purchase_quantity || 1)).toFixed(0)} {material.unit_of_measure || 'units'}
                            </span>
                            <div className={`text-xs ${theme.textSecondary}`}>
                              ({material.stock_quantity || 0} packs × {material.purchase_quantity})
                            </div>
                          </div>
                        ) : (
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                            (material.stock_quantity || 0) <= (material.low_stock_threshold || 5)
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                              : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          }`}>
                            {material.stock_quantity || 0} {material.unit_of_measure || 'units'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-bold ${theme.textPrimary}`}>
                        {material.pricing_type === 'bulk_purchase' && material.purchase_quantity ? (
                          <div className="flex flex-col">
                            <span className="text-2xl text-blue-600 dark:text-blue-400">
                              {((material.stock_quantity || 0) * (material.purchase_quantity || 1)).toFixed(0)}
                            </span>
                            <span className={`text-xs ${theme.textSecondary} font-normal mt-1`}>
                              {material.unit_of_measure || 'pieces'}
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col">
                            <span className="text-2xl text-gray-600 dark:text-gray-400">
                              {material.stock_quantity || 0}
                            </span>
                            <span className={`text-xs ${theme.textSecondary} font-normal mt-1`}>
                              {material.unit_of_measure || 'units'}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        material.is_standard 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {material.is_standard ? 'Standard' : 'Custom'}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}>
                      {material.order}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        material.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {material.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(material)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(material.id, material.name)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {materials.length === 0 && (
              <div className={`text-center py-8 ${theme.textSecondary}`}>
                No materials found. Add your first material to get started.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Capabilities Management Component
export const CapabilitiesManagement = () => {
  const theme = useThemeClasses();
  const [capabilities, setCapabilities] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCapability, setEditingCapability] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_standard: true,
    order: 0,
    is_active: true,
    unit_of_measure: 'units',
    stock_quantity: 0,
    low_stock_threshold: 5,
    pricing_type: 'per_unit',
    cost_per_unit: 0,
    purchase_quantity: null,
    purchase_price: null
  });

  useEffect(() => {
    loadCapabilities();
  }, []);

  const loadCapabilities = async () => {
    try {
      const response = await axios.get(`${API}/admin/capabilities`);
      setCapabilities(response.data);
    } catch (error) {
      console.error('Failed to load capabilities:', error);
      toast.error('Failed to load capabilities');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCapability) {
        await axios.put(`${API}/admin/capabilities/${editingCapability.id}`, formData);
        toast.success('Capability updated successfully');
      } else {
        await axios.post(`${API}/admin/capabilities`, formData);
        toast.success('Capability added successfully');
      }

      setFormData({ 
        name: '', 
        description: '', 
        is_standard: true, 
        order: 0, 
        is_active: true,
        unit_of_measure: 'units',
        stock_quantity: 0,
        low_stock_threshold: 5,
        pricing_type: 'per_unit',
        cost_per_unit: 0,
        purchase_quantity: null,
        purchase_price: null
      });
      setShowAddForm(false);
      setEditingCapability(null);
      loadCapabilities();
    } catch (error) {
      console.error('Failed to save capability:', error);
      toast.error('Failed to save capability');
    }
  };

  const handleEdit = (capability) => {
    setEditingCapability(capability);
    setFormData({
      name: capability.name,
      description: capability.description || '',
      is_standard: capability.is_standard,
      order: capability.order || 0,
      is_active: capability.is_active,
      unit_of_measure: capability.unit_of_measure || 'units',
      stock_quantity: capability.stock_quantity || 0,
      low_stock_threshold: capability.low_stock_threshold || 5,
      pricing_type: capability.pricing_type || 'per_unit',
      cost_per_unit: capability.cost_per_unit || 0,
      purchase_quantity: capability.purchase_quantity || null,
      purchase_price: capability.purchase_price || null
    });
    setShowAddForm(true);
  };

  const handleDelete = async (capabilityId, capabilityName) => {
    if (!window.confirm(`Are you sure you want to delete "${capabilityName}"? This action cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/admin/capabilities/${capabilityId}`);
      toast.success('Capability deleted successfully');
      loadCapabilities();
    } catch (error) {
      console.error('Failed to delete capability:', error);
      toast.error('Failed to delete capability');
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingCapability(null);
    setFormData({ 
      name: '', 
      description: '', 
      is_standard: true, 
      order: 0, 
      is_active: true,
      unit_of_measure: 'units',
      stock_quantity: 0,
      low_stock_threshold: 5,
      pricing_type: 'per_unit',
      cost_per_unit: 0,
      purchase_quantity: null,
      purchase_price: null
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Capabilities Management</h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          {showAddForm ? 'Cancel' : 'Add Capability'}
        </button>
      </div>

      {showAddForm && (
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-lg font-semibold mb-4">
            {editingCapability ? 'Edit Capability' : 'Add New Capability'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Capability Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="e.g., Layer resolution: 0.1mm - 0.3mm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Display Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  min="0"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Description (Optional)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                rows={3}
                placeholder="Brief description of this capability..."
              />
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_standard_cap"
                  checked={formData.is_standard}
                  onChange={(e) => setFormData({...formData, is_standard: e.target.checked})}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="is_standard_cap" className="text-sm font-medium">Standard Capability</label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active_cap"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="is_active_cap" className="text-sm font-medium">Active Capability</label>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <button
                type="submit"
                className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
              >
                {editingCapability ? 'Update Capability' : 'Add Capability'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className={`${theme.bgSecondary}0 text-white px-6 py-2 rounded hover:bg-gray-600`}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Capabilities Table */}
      <div className={`${theme.card} rounded-lg shadow overflow-hidden`}>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Capabilities ({capabilities.length})</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={`${theme.bgSecondary}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Name</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Description</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Type</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Order</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Status</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`${theme.card} divide-y divide-gray-200`}>
                {capabilities.map((capability) => (
                  <tr key={capability.id} className={`hover:bg-gray-100 dark:hover:bg-gray-700`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${theme.textPrimary}`}>{capability.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm ${theme.textPrimary} max-w-xs truncate`}>
                        {capability.description || 'No description'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        capability.is_standard 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {capability.is_standard ? 'Standard' : 'Custom'}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}>
                      {capability.order}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        capability.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {capability.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(capability)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(capability.id, capability.name)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {capabilities.length === 0 && (
              <div className={`text-center py-8 ${theme.textSecondary}`}>
                No capabilities found. Add your first capability to get started.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Flat Rate Shipping Management Component
export const FlatRateShippingManagement = () => {
  const theme = useThemeClasses();
  const [flatRates, setFlatRates] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0.0,
    min_weight: 0.0,
    max_weight: 999.0,
    weight_unit: 'lb',
    min_length: 0.0,
    max_length: 999.0,
    min_width: 0.0,
    max_width: 999.0,
    min_height: 0.0,
    max_height: 999.0,
    dimension_unit: 'inch',
    package_types: ['package'],
    delivery_time: '3-7 business days',
    is_active: true,
    order: 0
  });

  const packageTypeOptions = [
    'package', 'envelope', 'oversized', 'document', 
    'letter', 'postcard', 'tube', 'box'
  ];

  useEffect(() => {
    loadFlatRates();
  }, []);

  const loadFlatRates = async () => {
    try {
      const response = await axios.get(`${API}/admin/flat-rates`);
      setFlatRates(response.data);
    } catch (error) {
      console.error('Failed to load flat rates:', error);
      toast.error('Failed to load flat rate shipping options');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRate) {
        await axios.put(`${API}/admin/flat-rates/${editingRate.id}`, formData);
        toast.success('Flat rate updated successfully');
      } else {
        await axios.post(`${API}/admin/flat-rates`, formData);
        toast.success('Flat rate added successfully');
      }

      resetForm();
      loadFlatRates();
    } catch (error) {
      console.error('Failed to save flat rate:', error);
      toast.error('Failed to save flat rate');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: 0.0,
      min_weight: 0.0,
      max_weight: 999.0,
      weight_unit: 'lb',
      min_length: 0.0,
      max_length: 999.0,
      min_width: 0.0,
      max_width: 999.0,
      min_height: 0.0,
      max_height: 999.0,
      dimension_unit: 'inch',
      package_types: ['package'],
      delivery_time: '3-7 business days',
      is_active: true,
      order: 0
    });
    setShowAddForm(false);
    setEditingRate(null);
  };

  const handleEdit = (rate) => {
    setEditingRate(rate);
    setFormData({
      name: rate.name,
      description: rate.description || '',
      price: rate.price,
      min_weight: rate.min_weight,
      max_weight: rate.max_weight,
      weight_unit: rate.weight_unit,
      min_length: rate.min_length,
      max_length: rate.max_length,
      min_width: rate.min_width,
      max_width: rate.max_width,
      min_height: rate.min_height,
      max_height: rate.max_height,
      dimension_unit: rate.dimension_unit,
      package_types: rate.package_types || ['package'],
      delivery_time: rate.delivery_time,
      is_active: rate.is_active,
      order: rate.order || 0
    });
    setShowAddForm(true);
  };

  const handleDelete = async (rateId, rateName) => {
    if (!window.confirm(`Are you sure you want to delete "${rateName}"? This action cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/admin/flat-rates/${rateId}`);
      toast.success('Flat rate deleted successfully');
      loadFlatRates();
    } catch (error) {
      console.error('Failed to delete flat rate:', error);
      toast.error('Failed to delete flat rate');
    }
  };

  const handlePackageTypeChange = (packageType, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        package_types: [...prev.package_types, packageType]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        package_types: prev.package_types.filter(type => type !== packageType)
      }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Flat Rate Shipping Management</h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
        >
          {showAddForm ? 'Cancel' : 'Add Flat Rate'}
        </button>
      </div>

      {showAddForm && (
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-lg font-semibold mb-4">
            {editingRate ? 'Edit Flat Rate' : 'Add New Flat Rate'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Shipping Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="e.g., Standard Shipping, Express Shipping"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0.0})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="15.00"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                rows={3}
                placeholder="Description of this shipping option..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Delivery Time</label>
                <input
                  type="text"
                  value={formData.delivery_time}
                  onChange={(e) => setFormData({...formData, delivery_time: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="3-7 business days"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Display Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  min="0"
                />
              </div>
            </div>

            {/* Weight Constraints */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-3 text-blue-800">Weight Constraints</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Min Weight</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.min_weight}
                    onChange={(e) => setFormData({...formData, min_weight: parseFloat(e.target.value) || 0.0})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="0.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Max Weight</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.max_weight}
                    onChange={(e) => setFormData({...formData, max_weight: parseFloat(e.target.value) || 999.0})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="999.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Unit</label>
                  <select
                    value={formData.weight_unit}
                    onChange={(e) => setFormData({...formData, weight_unit: e.target.value})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="lb">pounds (lb)</option>
                    <option value="kg">kilograms (kg)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Dimension Constraints */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-3 text-green-800">Dimension Constraints</h4>
              <div className="grid grid-cols-4 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium mb-2">Max Length</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.max_length}
                    onChange={(e) => setFormData({...formData, max_length: parseFloat(e.target.value) || 999.0})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="999.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Max Width</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.max_width}
                    onChange={(e) => setFormData({...formData, max_width: parseFloat(e.target.value) || 999.0})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="999.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Max Height</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.max_height}
                    onChange={(e) => setFormData({...formData, max_height: parseFloat(e.target.value) || 999.0})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="999.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Unit</label>
                  <select
                    value={formData.dimension_unit}
                    onChange={(e) => setFormData({...formData, dimension_unit: e.target.value})}
                    className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="inch">inches</option>
                    <option value="cm">centimeters</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Package Types */}
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-3 text-yellow-800">Applicable Package Types</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {packageTypeOptions.map(packageType => (
                  <div key={packageType} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`pkg_${packageType}`}
                      checked={formData.package_types.includes(packageType)}
                      onChange={(e) => handlePackageTypeChange(packageType, e.target.checked)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor={`pkg_${packageType}`} className="text-sm capitalize">
                      {packageType}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active_rate"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="is_active_rate" className="text-sm font-medium">Active Shipping Option</label>
            </div>
            
            <div className="flex space-x-4">
              <button
                type="submit"
                className="bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700"
              >
                {editingRate ? 'Update Flat Rate' : 'Add Flat Rate'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className={`${theme.bgSecondary}0 text-white px-6 py-2 rounded hover:bg-gray-600`}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Flat Rates Table */}
      <div className={`${theme.card} rounded-lg shadow overflow-hidden`}>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Flat Rate Options ({flatRates.length})</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={`${theme.bgSecondary}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Name</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Price</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Weight Range</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Delivery</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Package Types</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Status</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`${theme.card} divide-y divide-gray-200`}>
                {flatRates.map((rate) => (
                  <tr key={rate.id} className={`hover:bg-gray-100 dark:hover:bg-gray-700`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className={`text-sm font-medium ${theme.textPrimary}`}>{rate.name}</div>
                        <div className={`text-sm ${theme.textSecondary}`}>{rate.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-green-600">${rate.price.toFixed(2)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${theme.textSecondary}`}>
                        {rate.min_weight} - {rate.max_weight} {rate.weight_unit}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${theme.textSecondary}`}>{rate.delivery_time}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {rate.package_types.map(type => (
                          <span key={type} className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${theme.bgSecondary} text-gray-800`}>
                            {type}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        rate.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {rate.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(rate)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rate.id, rate.name)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {flatRates.length === 0 && (
              <div className={`text-center py-8 ${theme.textSecondary}`}>
                No flat rate shipping options found. Add your first flat rate to get started.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};